import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Instagram, Facebook, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTACT, SERVICES } from "@/lib/services";
import Seo, { LOCAL_BUSINESS_LD, breadcrumbLd } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      toast.success("Thanks — your enquiry is on its way. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.message(detail);
      } else {
        toast.error("Something went wrong sending your enquiry. Please try calling us.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40">
      <Seo
        title="Contact Woodland Joinery — Sheffield Joiners & Builders | Free Quote"
        description="Get a free quote from Woodland Joinery & Building. Call Sean on +44 7856 925823 or Rocky on +44 7375 808008. Serving Sheffield, Rotherham, Chesterfield, Dronfield and Barnsley."
        path="/contact"
        jsonLd={[
          LOCAL_BUSINESS_LD,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20">
        <Reveal>
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
            Get in touch
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-[#1F3B2D] leading-[0.95] max-w-4xl">
            Free quotes across <em className="text-[#C8A24B] not-italic font-light">Sheffield &amp; beyond.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4A5550] leading-relaxed">
            Send us a quick message, or give Sean or Rocky a call directly. We
            aim to respond to every enquiry within 24 hours.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Info column */}
          <Reveal className="md:col-span-5 space-y-8">
            <div className="border border-[#E5E0D8] p-8 bg-white">
              <p className="text-[11px] tracking-[0.35em] uppercase text-[#C8A24B]">
                Call us direct
              </p>
              <div className="mt-6 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 border border-[#1F3B2D] flex items-center justify-center text-[#1F3B2D] shrink-0">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#4A5550]">
                      {CONTACT.sean.name}
                    </div>
                    <a
                      href={CONTACT.sean.tel}
                      data-testid="contact-phone-sean"
                      className="font-display text-2xl text-[#1F3B2D]"
                    >
                      {CONTACT.sean.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 border border-[#1F3B2D] flex items-center justify-center text-[#1F3B2D] shrink-0">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#4A5550]">
                      {CONTACT.rocky.name}
                    </div>
                    <a
                      href={CONTACT.rocky.tel}
                      data-testid="contact-phone-rocky"
                      className="font-display text-2xl text-[#1F3B2D]"
                    >
                      {CONTACT.rocky.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-[#E5E0D8] p-8 bg-white space-y-5">
              <div className="flex items-center gap-4">
                <Mail className="h-4 w-4 text-[#C8A24B]" />
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-email"
                  className="text-[#1A201C] link-underline"
                >
                  {CONTACT.email}
                </a>
              </div>
              <div className="flex items-center gap-4">
                <MapPin className="h-4 w-4 text-[#C8A24B]" />
                <span className="text-[#1A201C]">{CONTACT.area}</span>
              </div>
              <div className="flex items-center gap-4">
                <Clock className="h-4 w-4 text-[#C8A24B]" />
                <span className="text-[#1A201C]">Mon – Sat · 7:30am – 6:00pm</span>
              </div>
              <div className="pt-4 flex items-center gap-3">
                <a
                  href={CONTACT.socials.instagram}
                  aria-label="Instagram"
                  data-testid="contact-social-instagram"
                  className="h-10 w-10 border border-[#E5E0D8] flex items-center justify-center hover:border-[#C8A24B] hover:text-[#C8A24B] transition"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href={CONTACT.socials.facebook}
                  aria-label="Facebook"
                  data-testid="contact-social-facebook"
                  className="h-10 w-10 border border-[#E5E0D8] flex items-center justify-center hover:border-[#C8A24B] hover:text-[#C8A24B] transition"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Form column */}
          <Reveal delay={140} className="md:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-[#E5E0D8] p-8 md:p-10 bg-white"
              noValidate
            >
              <h2 className="font-display text-3xl md:text-4xl text-[#1F3B2D]">
                Request a free quote
              </h2>
              <p className="mt-2 text-[#4A5550]">
                Tell us a little about your project.
              </p>

              <div className="mt-8 grid gap-5 md:grid-cols-2">
                <Field label="Name" testId="contact-input-name">
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-input-name"
                    className="input-brand"
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email" testId="contact-input-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    className="input-brand"
                    placeholder="you@example.com"
                  />
                </Field>
                <Field label="Phone (optional)" testId="contact-input-phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-input-phone"
                    className="input-brand"
                    placeholder="07…"
                  />
                </Field>
                <Field label="Service" testId="contact-input-service">
                  <select
                    value={form.service}
                    onChange={update("service")}
                    data-testid="contact-input-service"
                    className="input-brand appearance-none bg-white"
                  >
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Other">Other / Not sure</option>
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Tell us about your project" testId="contact-input-message">
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    className="input-brand resize-none"
                    placeholder="A few lines about what you'd like us to build…"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-form-submit"
                className="btn-primary mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send Enquiry"}
                <Send className="h-4 w-4" />
              </button>

              <p className="mt-4 text-xs text-[#4A5550]">
                We’ll only use your details to reply to your enquiry.
              </p>
            </form>
          </Reveal>
        </div>
      </section>

      <style>{`
        .input-brand {
          width: 100%;
          padding: 14px 16px;
          background: #FDFBF7;
          border: 1px solid #E5E0D8;
          color: #1A201C;
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color 200ms ease, background 200ms ease;
        }
        .input-brand::placeholder { color: #98a09b; }
        .input-brand:focus {
          border-color: #1F3B2D;
          background: #ffffff;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.25em] uppercase text-[#4A5550] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
